/**
 * Task #3408 — App Startup & Loading Experience Overhaul
 *
 * Role-based data prefetch: immediately after auth resolves, kick off prefetch
 * for the most likely first-page API calls. Uses queryClient.prefetchQuery so
 * data is in the cache before the route component mounts.
 *
 * Connection-aware: skips prefetch on saveData or slow-2g/2g connections so
 * metered users don't waste bandwidth on speculative downloads.
 */
import type { QueryClient } from "@tanstack/react-query";

function isConstrainedConnection(): boolean {
  const conn = (navigator as any).connection;
  if (!conn) return false;
  if (conn.saveData) return true;
  if (conn.effectiveType === "slow-2g" || conn.effectiveType === "2g") return true;
  return false;
}

const PREFETCH_STALE_TIME = 5 * 60 * 1000; // 5 min

export function prefetchForRole(role: string, queryClient: QueryClient): void {
  if (isConstrainedConnection()) return;

  const prefetch = (queryKey: string[]) => {
    queryClient.prefetchQuery({
      queryKey,
      staleTime: PREFETCH_STALE_TIME,
    });
  };

  switch (role) {
    case "admin":
    case "tenant_admin":
      prefetch(["/api/dashboard/summary"]);
      prefetch(["/api/navigation-config"]);
      break;

    case "ptw_supervisor":
    case "permit_issuer":
    case "hse_officer":
      prefetch(["/api/ptw-v2/permits", "status=active&limit=20"]);
      break;

    case "super_admin":
      prefetch(["/api/super-admin/tenants", "limit=20"]);
      break;

    case "worker":
      prefetch(["/api/worker/profile"]);
      break;

    default:
      if (role?.startsWith("partner_")) {
        prefetch(["/api/partner/overview"]);
      }
      break;
  }
}
